import { ANNOTATION_DATA_TYPE_INT } from '../../../../constants/index.js';
import type {
  AnnotationDataEntry,
  AnnotationSchema,
  AnnotationSet,
  DataEntry,
} from '../../../../services/dataHubService.interface.js';
import type { DataHubService } from '../../../../services/dataHubService.js';
import type { UsageContextOverrides } from '../../../../services/usageContext.interface.js';
import {
  USAGE_EXTRA_KEYS,
  withCorrelationId,
} from '../../../../services/usageContext.js';
import { classifyScenarioError } from '../../../../telemetry/scenarioError.js';
import type { IScenario } from '../../../../telemetry/scenarios.interface.js';
import { runConcurrent, withRetry } from '../../../../utils/concurrency.js';

// ── Types ──────────────────────────────────────────────────────────────────

export interface UploadProgress {
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  currentSubfolder: string;
  phase: 'preparing' | 'uploading' | 'done';
}

export interface UploadResult {
  success: boolean;
  errors: string[];
  createdEntryIds: string[];
  uploadedFileCount: number;
}

interface SubfolderUploadTask {
  subfolderName: string;
  files: File[];
  matchedEntry: DataEntry;
}

// ── Constants ──────────────────────────────────────────────────────────────

const CONCURRENT_FILE_UPLOADS = 5;
const MAX_RETRIES = 3;

// ── File grouping ─────────────────────────────────────────────────────────

export function groupFilesBySubfolder(files: FileList): Map<string, File[]> {
  const subfolderFiles = new Map<string, File[]>();
  for (const file of Array.from(files)) {
    const parts = file.webkitRelativePath.split('/');
    if (parts.length < 3) continue;
    const subfolderName = parts[1]!;
    const group = subfolderFiles.get(subfolderName) ?? [];
    group.push(file);
    subfolderFiles.set(subfolderName, group);
  }
  return subfolderFiles;
}

// ── Schema name resolver ──────────────────────────────────────────────────

function resolveSchemaName(
  schemaId: string,
  schemas: AnnotationSchema[],
): string {
  const found = schemas.find((s) => s.id === schemaId);
  return found?.name ?? schemaId;
}

// ── Single file upload (with retry) ───────────────────────────────────────

async function uploadSingleFile(
  service: DataHubService,
  dataSetId: string,
  annotationSetId: string,
  dataId: string,
  file: File,
  taskNumber: number,
  fileNumber: number,
  scenario?: IScenario,
): Promise<void> {
  let attemptNumber = 0;
  await withRetry(async () => {
    attemptNumber++;
    const markData = {
      task_number: taskNumber,
      file_number: fileNumber,
      attempt_number: attemptNumber,
    };
    scenario?.mark('annotation_upload_url_requested', markData);
    const { fileId, uploadUrl } = await service.annotationCreateUploadUrl(
      dataSetId,
      annotationSetId,
      dataId,
      file.name,
    );
    scenario?.mark('annotation_upload_url_generated', markData);

    scenario?.mark('annotation_blob_upload_started', markData);
    await service.uploadFileToBlob(uploadUrl, file);
    scenario?.mark('annotation_blob_uploaded', markData);

    scenario?.mark('annotation_file_commit_started', markData);
    await service.annotationCompleteUpload(dataSetId, annotationSetId, dataId, {
      id: fileId,
      fileName: file.name,
      fileType: 'generic',
      fileSizeInBytes: file.size,
    });
    scenario?.mark('annotation_file_committed', markData);
  }, MAX_RETRIES);
}

// ── Main upload orchestrator ──────────────────────────────────────────────

export async function uploadAnnotationFolder(
  service: DataHubService,
  dataSetId: string,
  annotationSetId: string,
  matchedSet: AnnotationSet,
  schemas: AnnotationSchema[],
  subfolderFiles: Map<string, File[]>,
  dataEntries: DataEntry[],
  onProgress: (progress: UploadProgress) => void,
  scenario?: IScenario,
): Promise<UploadResult> {
  const entryByName = new Map(
    dataEntries
      .filter((e) => e.name != null)
      .map((e) => [e.name!.toLowerCase(), e]),
  );

  const category = resolveSchemaName(matchedSet.schemaId, schemas);
  const errors: string[] = [];
  const createdEntryIds: string[] = [];

  // Build list of valid subfolder tasks, collecting errors for unmatched ones
  const tasks: SubfolderUploadTask[] = [];
  for (const [subfolderName, files] of subfolderFiles) {
    const matchedEntry = entryByName.get(subfolderName.toLowerCase());
    if (!matchedEntry) {
      errors.push(`"${subfolderName}": no matching data entry found`);
      continue;
    }
    tasks.push({ subfolderName, files, matchedEntry });
  }

  // Count total files across all valid tasks
  const totalFiles = tasks.reduce((sum, t) => sum + t.files.length, 0);
  let completedFiles = 0;
  let failedFiles = 0;

  // Milestone: subfolder-to-entry matching done; per-entry uploads begin next.
  scenario?.mark('tasks_prepared', {
    subfolder_count: tasks.length,
    total_files: totalFiles,
  });

  // One summary usage event for the whole bulk upload (D2): `batchCreateAnnotationData`
  // is called once per subfolder below, so we attach the usage context to only
  // the FIRST call and omit it on the rest. `entryCount` = number of subfolders
  // (annotation entries created), `fileCount` = total files across them.
  const uploadUsage: UsageContextOverrides = {
    sourceId: annotationSetId,
    extras: withCorrelationId(scenario?.queryId, {
      [USAGE_EXTRA_KEYS.ENTRY_COUNT]: String(tasks.length),
      [USAGE_EXTRA_KEYS.FILE_COUNT]: String(totalFiles),
    }),
  };
  let usageAttached = false;

  const reportProgress = (currentSubfolder: string): void => {
    onProgress({
      totalFiles,
      completedFiles,
      failedFiles,
      currentSubfolder,
      phase: 'uploading',
    });
  };

  onProgress({
    totalFiles,
    completedFiles: 0,
    failedFiles: 0,
    currentSubfolder: '',
    phase: 'preparing',
  });

  // Process each subfolder: create annotation data entry, then upload files in parallel
  for (const [taskIndex, task] of tasks.entries()) {
    const taskNumber = taskIndex + 1;
    reportProgress(task.subfolderName);

    let createdEntry: AnnotationDataEntry | undefined;
    try {
      scenario?.mark('annotation_entry_create_started', {
        task_number: taskNumber,
        file_count: task.files.length,
      });
      // Attach the single summary usage event to the first batch call only.
      const usage = usageAttached ? undefined : uploadUsage;
      usageAttached = true;
      const batchResult = await service.batchCreateAnnotationData(
        dataSetId,
        annotationSetId,
        [
          {
            dataType: ANNOTATION_DATA_TYPE_INT,
            name: task.subfolderName,
            rawDataId: task.matchedEntry.dataId,
            category,
          },
        ],
        usage,
      );

      if (batchResult.failedCount > 0) {
        scenario?.mark('annotation_entry_create_failed', {
          task_number: taskNumber,
          failed_file_count: task.files.length,
        });
        const failMsg =
          batchResult.failedItems[0]?.errorMessage ?? 'Unknown error';
        errors.push(`"${task.subfolderName}": ${failMsg}`);
        failedFiles += task.files.length;
        reportProgress(task.subfolderName);
        continue;
      }

      createdEntry = batchResult.succeededItems[0];
      if (!createdEntry) {
        scenario?.mark('annotation_entry_create_failed', {
          task_number: taskNumber,
          failed_file_count: task.files.length,
        });
        errors.push(
          `"${task.subfolderName}": no entry returned after creation`,
        );
        failedFiles += task.files.length;
        reportProgress(task.subfolderName);
        continue;
      }
      scenario?.mark('annotation_entry_created', {
        task_number: taskNumber,
      });
    } catch (err) {
      scenario?.mark('annotation_entry_create_failed', {
        task_number: taskNumber,
        failed_file_count: task.files.length,
        ...classifyScenarioError(err).data,
      });
      errors.push(
        `"${task.subfolderName}": ${err instanceof Error ? err.message : 'Failed to create entry'}`,
      );
      failedFiles += task.files.length;
      reportProgress(task.subfolderName);
      continue;
    }

    createdEntryIds.push(createdEntry.dataId);

    // Upload files in this subfolder concurrently
    const completedBeforeTask = completedFiles;
    const failedBeforeTask = failedFiles;
    scenario?.mark('annotation_files_upload_started', {
      task_number: taskNumber,
      file_count: task.files.length,
    });
    await runConcurrent(
      task.files,
      CONCURRENT_FILE_UPLOADS,
      async (file: File, fileIndex: number) => {
        try {
          await uploadSingleFile(
            service,
            dataSetId,
            annotationSetId,
            createdEntry.dataId,
            file,
            taskNumber,
            fileIndex + 1,
            scenario,
          );
          completedFiles++;
        } catch (err) {
          failedFiles++;
          scenario?.mark('annotation_file_upload_failed', {
            task_number: taskNumber,
            file_number: fileIndex + 1,
            ...classifyScenarioError(err).data,
          });
          errors.push(
            `"${task.subfolderName}/${file.name}": ${err instanceof Error ? err.message : 'Upload failed'} (after ${MAX_RETRIES} retries)`,
          );
        }
        reportProgress(task.subfolderName);
      },
    );
    scenario?.mark('annotation_files_uploaded', {
      task_number: taskNumber,
      completed_file_count: completedFiles - completedBeforeTask,
      failed_file_count: failedFiles - failedBeforeTask,
    });
  }

  // Best-effort rollback: delete created entries when the upload had failures
  if (errors.length > 0 && createdEntryIds.length > 0) {
    let rollbackFailures = 0;
    scenario?.mark('rollback_started', {
      entry_count: createdEntryIds.length,
    });
    for (const dataId of createdEntryIds) {
      try {
        await service.deleteAnnotationData(dataSetId, annotationSetId, dataId);
      } catch (cleanupErr) {
        rollbackFailures++;
        errors.push(
          `Rollback failed for entry "${dataId}": ${cleanupErr instanceof Error ? cleanupErr.message : 'Unknown error'}`,
        );
      }
    }
    scenario?.mark('rollback_completed', {
      entry_count: createdEntryIds.length,
      failed_entry_count: rollbackFailures,
    });
  }

  // Milestone: all annotation entries created and blob uploads settled.
  scenario?.mark('files_uploaded', {
    completed_files: completedFiles,
    failed_files: failedFiles,
  });

  onProgress({
    totalFiles,
    completedFiles,
    failedFiles,
    currentSubfolder: '',
    phase: 'done',
  });

  return {
    success: errors.length === 0,
    errors,
    createdEntryIds,
    uploadedFileCount: completedFiles,
  };
}
